import {createSelector} from 'reselect';
import {sort} from 'ramda';

import {getActiveProduct} from 'selectors/product/product';
import {ApplicationStoreType} from 'reducers';
import {getCountOfBoxes} from 'features/contacts/selectors/prescription/prescription';
import {
    TierPricesMSType,
    ProductType,
    SimpleProductLensesMSType,
} from 'constants/flow/flowTypesProduct';
import {isConfigurePage} from 'selectors/routerInfo/routerInfo';
import {getItemV2} from 'pages/cart/store/selectors/itemV2/itemV2';
import {hasUserPreviousOrderCl, isReorderAvailable} from 'features/reorder/selectors/reorder';
import {getCategoryItemImageElastic} from 'libs/category/category';
import {CONTACTS_DEFAULT_COUPON, RETURNING_CUSTOMER_COUPON} from 'constants/contacts/contacts';

export const PLA = 'PLA';

/**
 * Get product images
 * order will change if it page contact lenses
 * @return {Array[string]} - product images
 */
export const getProductImages = createSelector(
    getActiveProduct,
    (product: SimpleProductLensesMSType): Array<string> => {
        return (product.isContactLenses
            ? [
                  getCategoryItemImageElastic(product, 'front'),
                  getCategoryItemImageElastic(product, 'side'),
              ]
            : [
                  getCategoryItemImageElastic(product, 'angle'),
                  getCategoryItemImageElastic(product, 'front'),
                  getCategoryItemImageElastic(product, 'side'),
              ]
        ).filter(Boolean);
    },
);

/**
 * Get final price and tierPrices price
 * @return {finalPrice: number, tierPrices: Array<TierPricesMSType>} - product images
 */
export const getFinalPriceAndTierPricesPrice = createSelector(
    getActiveProduct as () => SimpleProductLensesMSType,
    getItemV2,
    isConfigurePage,
    (
        {finalPrice, tierPrices = []},
        {item},
        isConfigure,
    ): {
        finalPrice: number;
        tierPricesSorted: Array<TierPricesMSType>;
    } => {
        const tierPricesSorted = sort(
            (a: TierPricesMSType, b: TierPricesMSType) => a.qty - b.qty,
            tierPrices,
        );
        return isConfigure
            ? {finalPrice: item?.discountedPrice || 0, tierPricesSorted}
            : {finalPrice, tierPricesSorted};
    },
);

/**
 * Returns the final price per box based on tierPrices
 */
export const getFinalPricePerBox = createSelector(
    getCountOfBoxes,
    getFinalPriceAndTierPricesPrice,
    (lensesBoxes, {finalPrice, tierPricesSorted}): number => {
        const nearTierPricesToCountOfBoxes = tierPricesSorted.reduce(
            (prevQty: number, curQty: TierPricesMSType): number => {
                if (lensesBoxes === curQty.qty) {
                    return curQty.qty;
                }
                if (lensesBoxes >= prevQty && lensesBoxes <= curQty.qty) {
                    return prevQty;
                }
                return curQty.qty;
            },
            0,
        );
        const currentTierPrices = tierPricesSorted.find(
            tierPrices => tierPrices.qty === nearTierPricesToCountOfBoxes,
        );
        // in some cases, we will get tierPrices, but price field will be empty, so we need to return the original price
        if (!currentTierPrices || !currentTierPrices.price) return finalPrice;
        return currentTierPrices.price;
    },
);

/**
 * it returns true if product sku contains PLA and false otherwise
 */
export const isContactLensesPLA = createSelector(
    getActiveProduct,
    (product: ProductType | boolean): boolean => {
        if (typeof product === 'boolean') {
            return false;
        }
        return Boolean(product.sku) && product.sku.match(PLA) !== null;
    },
);

/**
 * Return default coupon if reorder is available
 * @param {ApplicationStoreType} store - redux
 * @param {string} coupon - promocode
 * @returns {string} - updated coupon
 */
export const getReorderCoupon = (store: ApplicationStoreType, coupon: string): string => {
    if (coupon.toLocaleLowerCase() === CONTACTS_DEFAULT_COUPON && isReorderAvailable(store))
        return RETURNING_CUSTOMER_COUPON;
    return coupon;
};

/**
 * Takes coupon code for discount price
 * @param {ApplicationStoreType} store - redux
 * @returns {string} - promocode
 */
export const getCoupon = createSelector(hasUserPreviousOrderCl, isReturningCustomer =>
    isReturningCustomer ? RETURNING_CUSTOMER_COUPON : CONTACTS_DEFAULT_COUPON,
);
